import {
  Box,
  Button,
  Editor,
  Field,
  FieldArray,
  Form,
  Grid,
  LogoUpload,
  Notification,
  TextField,
  Typography,
} from "components";
import { compose, withFormik, withHooks, withTranslation } from "enhancers";
import withPreventLeaveDirtyForm from "enhancers/withPreventLeaveDirtyForm";
import { PageContent } from "layouts";
import {
  getErrorMessage,
  gql,
  homePath,
  notifyError,
  paths,
  Yup,
} from "utils/helper";
import { AppColor } from "theme/app-color";
import { isEmpty } from "lodash";

interface MouSettingFormData {
  mouName: string;
  mouPosition: string;
  signatureImage?: File | null;
  governmentBudgetText: string;
  nonGovernmentBudgetText: string;
  governmentNonCovertText: string;
  nonGovernmentNonCovertText: string;
}

const initialValues: MouSettingFormData = {
  mouName: "",
  mouPosition: "",
  signatureImage: null,
  governmentBudgetText: "",
  governmentNonCovertText: "",
  nonGovernmentBudgetText: "",
  nonGovernmentNonCovertText: "",
};

const validationSchema = Yup.object().shape({
  mouName: Yup.string().required("ต้องไม่เว้นว่างไว้"),
  mouPosition: Yup.string().required("ต้องไม่เว้นว่างไว้"),
  signatureImage: Yup.mixed().required("ต้องไม่เว้นว่างไว้"),
});

// GraphQL API definitions
const API = {
  SYSTEM_SETTINGS: gql`
    query SYSTEM_SETTINGS {
      systemSettings {
        mouName
        mouPosition
        signatureImage {
          url
          fileName
        }
        governmentBudgetText
        governmentNotCoveredText
        nonGovernmentBudgetText
        nonGovernmentNotCoveredText
      }
    }
  `,
  UPSERT_SYSTEM_SETTINGS: gql`
    mutation UPSERT_SYSTEM_SETTINGS(
      $mouName: String
      $mouPosition: String
      $signatureImage: [Upload!]!
      $governmentBudgetText: String
      $governmentNotCoveredText: String
      $nonGovernmentBudgetText: String
      $nonGovernmentNotCoveredText: String
    ) {
      upsertSystemSettings(
        input: {
          mouName: $mouName
          mouPosition: $mouPosition
          signatureImage: $signatureImage
          governmentBudgetText: $governmentBudgetText
          governmentNotCoveredText: $governmentNotCoveredText
          nonGovernmentBudgetText: $nonGovernmentBudgetText
          nonGovernmentNotCoveredText: $nonGovernmentNotCoveredText
        }
      ) {
        id
      }
    }
  `,
};

const SettingTextSection = ({
  title,
  leftLabel,
  leftName,
  rightLabel,
  rightName,
}: any) => {
  return (
    <>
      <Grid item xs={12}>
        <Typography variant="h4">{title}</Typography>
      </Grid>

      <Grid item xs={6}>
        <Field
          component={Editor}
          name={leftName}
          label={leftLabel}
          labelVariant="Body/20"
          required
          placeholder="โปรดระบุ..."
        />
      </Grid>

      <Grid item xs={6}>
        <Field
          component={Editor}
          name={rightName}
          label={rightLabel}
          labelVariant="Body/20"
          required
          placeholder="โปรดระบุ..."
        />
      </Grid>
    </>
  );
};

const SettingTexts = [
  {
    title: "ข้อความสำหรับโรงพยาบาลรัฐบาล",
    leftLabel: "ส่วนที่ 1: ข้อมูลเกี่ยวกับวงเงินตามสิทธิ์",
    leftName: "governmentBudgetText",
    rightLabel: "ส่วนที่ 2: ข้อมูลเกี่ยวกับค่าใช้จ่ายส่วนเกิน",
    rightName: "governmentNotCoveredText",
  },
  {
    title: "ข้อความสำหรับโรงพยาบาลเอกชน",
    leftLabel: "ส่วนที่ 1: ข้อมูลเกี่ยวกับวงเงินตามสิทธิ์",
    leftName: "nonGovernmentBudgetText",
    rightLabel: "ส่วนที่ 2: ข้อมูลเกี่ยวกับค่าใช้จ่ายส่วนเกิน",
    rightName: "nonGovernmentNotCoveredText",
  },
];

const descriptionText = [
  {
    text: "จำนวนวงเงิน IPD OPD คงเหลือตามสิทธิ์บริษัทให้ใส่คำว่า",
    value: "{{REMAINING_IPD_OPD_BUDGET}}",
  },
  {
    text: "จำนวนวงเงินทันตกรรรมคงเหลือตามสิทธิ์บริษัทให้ใส่คำว่",
    value: "{{REMAINING_DENTAL_BUDGET}}",
  },
  {
    text: "จำนวนครั้งขูดหินปูนคงเหลือตามสิทธิ์บริษัทให้ใส่คำว่า",
    value: "{{REMAINING_DENTAL_TIME}}",
  },
];

const PdfSettingPage = (props: any) => {
  return (
    <PageContent
      title={props.title}
      breadcrumbs={props.breadcrumbs}
      loading={props.loading}
    >
      <Form>
        <Box>
          <Typography variant="h4" style={{ marginBottom: 32 }}>
            {props.t(".mouSetting")}
          </Typography>

          <Grid container spacing={8}>
            <Grid item xs={12} md={6}>
              <Field
                component={TextField}
                name="mouName"
                label={props.t(".name")}
                required
                fullWidth
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Field
                component={TextField}
                name="mouPosition"
                label={props.t(".position")}
                required
                fullWidth
              />
            </Grid>

            <Grid item xs={6}>
              <Box
                width="100%"
                display="flex"
                alignItems={props.hasImage ? "flex-start" : "flex-end"}
                flexDirection="column"
                style={{ gap: props.hasImage ? 0 : 12 }}
              >
                <FieldArray
                  label={props.t(".signatureImage")}
                  component={LogoUpload}
                  name="signatureImage"
                  noStartIcon
                  hasEditPermission
                  addButtonLabel={props.t(".upload")}
                  showEditButton={false}
                  removeSingleImage={true}
                  required
                  style={{ width: "100%", height: 300 }}
                />
                {!props.hasImage && (
                  <Typography
                    variant="Helper/12"
                    color={AppColor["Text/Secondary"]}
                    style={{ marginBottom: 16 }}
                  >
                    {props.t(".signatureImageNote")}
                  </Typography>
                )}
              </Box>
            </Grid>

            {SettingTexts.map((item) => (
              <SettingTextSection key={item.title} {...item} />
            ))}

            <Grid item xs={12}>
              <Typography variant="Body/16" color={AppColor["Text/Secondary"]}>
                {
                  "*สามารถระบุตัวค่าตัวเลขจากระบบซึ่งแตกต่างกันตามพนักงานแต่ละคนลงในข้อความได้ ดังนี้"
                }
              </Typography>
              <ul style={{ margin: 0 }}>
                {descriptionText.map((item) => (
                  <li>
                    <Typography
                      variant="Body/16"
                      color={AppColor["Text/Secondary"]}
                    >
                      {item.text}{" "}
                      <span
                        style={{
                          fontWeight: 600,
                          color: AppColor["Primary/Primary"],
                        }}
                      >
                        {item.value}
                      </span>
                    </Typography>
                  </li>
                ))}
              </ul>
              <br />
              <Typography variant="Body/16">
                {
                  "เช่น วงเงินสวัสดิการพนักงานคงเหลือ {{REMAINING_IPD_OPD_BUDGET}} บาท"
                }
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Box display="flex" justifyContent="flex-start" mt={4}>
                <Button
                  variant="outlined"
                  onClick={props.handleCancel}
                  style={{ marginRight: 16 }}
                >
                  {props.t(".cancel")}
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  type="submit"
                  disabled={!props.enabledSubmitButton}
                >
                  {props.t(".save")}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Form>
    </PageContent>
  );
};

const enhancer = compose(
  withFormik({
    initialValues,
    validationSchema,
  }),
  withPreventLeaveDirtyForm(),
  withTranslation({ prefix: "pages.main.pdfSetting" }),
  withHooks((props: any, hooks: any) => {
    const { t, dirty, isValid, setInitialValues, initialValues } = props;
    const {
      useMemo,
      useCallback,
      useHandleSubmit,
      useQuery,
      useMutation,
      useEffect,
      useState,
    } = hooks;

    const title = t(".title");

    const breadcrumbs = useMemo(
      () => [
        { path: homePath(), label: t(".home") },
        { path: paths.partnerHospitalPath(), label: t(".partnerHospital") },
        { path: null, label: title },
      ],
      [title, t]
    );

    // Fetch existing system settings
    const { loading, data, error, refetch } = useQuery(API.SYSTEM_SETTINGS, {
      fetchPolicy: "network-only",
    });

    const [isInitialLoading, setIsInitialLoading] = useState(true);

    const [upsertSystemSettings] = useMutation(API.UPSERT_SYSTEM_SETTINGS);

    const convertImagesToFilesObject = useCallback(async (images: any[]) => {
      const result = await Promise.all(
        images.map(async (img: any) => {
          if (img.fileName && img.url) {
            const blob = await fetch(img.url).then((r) => r.blob());
            const file = new File([blob], img.fileName, {
              type: blob.type,
            });
            return file;
          } else {
            return img;
          }
        })
      );
      return result;
    }, []);

    const setInitialAsync = useCallback(async () => {
      if (data?.systemSettings && !loading && !error) {
        const {
          mouName,
          mouPosition,
          signatureImage,
          governmentBudgetText,
          governmentNotCoveredText,
          nonGovernmentBudgetText,
          nonGovernmentNotCoveredText,
        } = data?.systemSettings;
        setInitialValues({
          mouName: mouName || "",
          mouPosition: mouPosition || "",
          signatureImage: await convertImagesToFilesObject(signatureImage),
          governmentBudgetText: governmentBudgetText || "",
          governmentNotCoveredText: governmentNotCoveredText || "",
          nonGovernmentBudgetText: nonGovernmentBudgetText || "",
          nonGovernmentNotCoveredText: nonGovernmentNotCoveredText || "",
        });
      }
    }, [
      data?.systemSettings,
      loading,
      error,
      setInitialValues,
      convertImagesToFilesObject,
    ]);

    useEffect(() => {
      setInitialAsync();
    }, [setInitialAsync]);

    useHandleSubmit(
      async (values: MouSettingFormData) => {
        try {
          await upsertSystemSettings({
            variables: values,
          });
          Notification.success(t(".saveSuccess"));
          paths.partnerHospitalPath().push();
        } catch (error) {
          notifyError(getErrorMessage(error));
        }
      },
      [upsertSystemSettings]
    );

    const enabledSubmitButton =
      dirty && isValid && props.values.signatureImage?.length > 0;

    const handleCancel = useCallback(() => {
      paths.partnerHospitalPath().push();
    }, []);

    const hasImage = useMemo(() => {
      return props.values.signatureImage?.length > 0;
    }, [props.values.signatureImage]);

    const isLoading = useMemo(() => {
      return loading || isInitialLoading;
    }, [loading, isInitialLoading]);

    useEffect(() => {
      if (!isEmpty(initialValues) && !loading) {
        setIsInitialLoading(false);
      } else if (data?.systemSettings === null) {
        setIsInitialLoading(false);
      }
    }, [initialValues, data?.systemSettings, loading]);

    return {
      title,
      breadcrumbs,
      enabledSubmitButton,
      handleCancel,
      hasImage,
      t,
      loading: isLoading,
    };
  })
);

export default enhancer(PdfSettingPage);
